/**
 * Conversion / Win-Rate metrics (REQ-RPT-01)
 *
 * Single source of truth for "how many quotes did we win, as a % of total".
 * Historically two inline computations disagreed:
 *   - ReportsOverview counted only 'accepted' | 'won' as conversions
 *   - reportDefinitions.quote_performance counted 'accepted' | 'acknowledged' | 'won'
 * Neither counted quotes that were 'converted' (turned into a job) as conversions,
 * so a quote that progressed all the way to a job actually dropped OUT of the rate.
 *
 * This helper treats any "won" outcome — accepted, converted, won (and the
 * existing 'acknowledged' synonym) — as a conversion, divided by total quotes.
 */

// Statuses that represent a won/converted quote.
export const CONVERSION_STATUSES = ['accepted', 'converted', 'won', 'acknowledged'];

/**
 * True if a single quote should count as a conversion.
 * Counts by status OR by the presence of a converted_to_job_id link.
 * @param {object} quote
 * @returns {boolean}
 */
export function isConverted(quote) {
  if (!quote) return false;
  if (quote.converted_to_job_id) return true;
  return CONVERSION_STATUSES.includes(quote.status);
}

/**
 * Number of quotes that count as conversions.
 * @param {Array<object>} quotes
 * @returns {number}
 */
export function conversionCount(quotes) {
  if (!Array.isArray(quotes)) return 0;
  return quotes.filter(isConverted).length;
}

/**
 * Conversion / win rate as a whole-number percentage (0–100), rounded.
 * Denominator is total quotes (matches both existing call sites).
 * Returns 0 when there are no quotes.
 * @param {Array<object>} quotes
 * @returns {number}
 */
export function conversionRate(quotes) {
  if (!Array.isArray(quotes) || quotes.length === 0) return 0;
  return Math.round((conversionCount(quotes) / quotes.length) * 100);
}
